/*
 * Live additive bridge for LED grille-badge model selection.
 *
 * This file deliberately does not change the shared vehicle resolver or search
 * engine. It only reuses the already rendered live badge cards in the ordinary
 * accessories hub after that hub has finished its native model update.
 */
(() => {
  'use strict';

  const MODEL_ONLY_DESIGNS = new Set(['g63', 'g55']);

  const normalize = value => String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const brandKey = value => normalize(value).replace(/^mercedesbenz$/, 'mercedes');

  const selectedModelKey = value => normalize(value);

  const cardHandle = card => {
    const explicit = normalize(card.dataset.grilleBadgeModel);
    if (explicit) return explicit;
    const link = card.querySelector('a[href*="/products/"]');
    const href = link ? link.getAttribute('href') || '' : '';
    const handle = normalize(href.split('?')[0].split('/').filter(Boolean).pop());
    const text = normalize(card.textContent);
    const source = `${handle}${text}`;
    if (source.includes('g63')) return 'g63';
    if (source.includes('g55')) return 'g55';
    return '';
  };

  const isKnownModelCard = card => MODEL_ONLY_DESIGNS.has(cardHandle(card));

  const setHidden = (node, hidden) => {
    node.hidden = hidden;
    if (hidden) node.setAttribute('aria-hidden', 'true');
    else node.removeAttribute('aria-hidden');
  };

  const sourceCards = hub => [...hub.querySelectorAll(
    '[data-hub-grille-grid] [data-lds-grille-badge-card], [data-hub-grille-grid] [data-lds-hub-kind="grille-badge"], #lds-accessories-grille [data-lds-grille-showcase], #lds-accessories-grille [data-grille-showcase-designs] [data-lds-grille-badge-card], #lds-accessories-grille [data-grille-showcase-designs] [data-grille-dev-draft]'
  )].filter(card => !(card.matches('[data-lds-grille-badge-card]')
    && card.closest('[data-hub-grille-grid] [data-lds-hub-kind="grille-badge"]')));

  const filterKnownModelCards = hub => {
    sourceCards(hub).forEach(card => setHidden(card, false));
  };

  const allowedCardsForSelection = (hub, selectedLabel) => {
    const brand = brandKey(hub.dataset.ldsHubBrand);
    const selected = selectedModelKey(selectedLabel);
    return sourceCards(hub).filter(card => {
      let routing = null;
      try { routing = JSON.parse(card.dataset.grilleBadgeRouting || 'null'); } catch (_error) { routing = null; }
      const scope = routing?.scope || card.dataset.grilleBadgeScope || '';
      const modelTokens = Array.isArray(routing?.modelTokens) ? routing.modelTokens : String(card.dataset.grilleBadgeModelTokens || '').split(/[,|]/u).filter(Boolean);
      if (scope === 'brand-common') return true;
      if (scope === 'brand-only') return selected === '';
      if (scope === 'model' && modelTokens.length) {
        const selectedText = String(selectedLabel).normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        return modelTokens.some(token => {
          const normalized = String(token).normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
          return normalized && (` ${selectedText} `).includes(` ${normalized} `);
        });
      }
      if (!scope && card.dataset.grilleBadgeModel) {
        const selectedText = String(selectedLabel).normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        const normalized = String(card.dataset.grilleBadgeModel).normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        return normalized && (` ${selectedText} `).includes(` ${normalized} `);
      }
      const model = cardHandle(card);
      if (brand === 'bmw') {
        // BMW ///M is a brand design, not a model-specific M-version.
        return model === '';
      }
      if (brand !== 'mercedes') return false;
      if (model === 'g63' || model === 'g55') return String(selectedLabel).toLowerCase().split(/[^a-z0-9]+/).includes(model);
      // A blank Mercedes model scope is not treated as universal here. The
      // current AMG/G700 source designs are brand-level source designs, not confirmed
      // common Mercedes-Benz model compatibility. A future card can opt in by
      // carrying the explicit scope marker.
      return card.dataset.grilleBadgeScope === 'brand-common';
    });
  };

  const removeBridge = hub => {
    hub.querySelectorAll('[data-lds-grille-model-result]').forEach(node => node.remove());
    const section = hub.querySelector('#lds-accessories-grille');
    if (section) setHidden(section, false);
  };

  const renderBridge = hub => {
    filterKnownModelCards(hub);
    const selectedNode = hub.querySelector('[data-hub-selected-label]');
    const selectedLabel = selectedNode?.textContent?.trim() || '';
    const output = hub.querySelector('[data-hub-filtered-badges]');
    const section = hub.querySelector('#lds-accessories-grille');
    removeBridge(hub);
    if (!selectedLabel || !output) return;

    const cards = allowedCardsForSelection(hub, selectedLabel);
    if (!cards.length) { if (section) setHidden(section, true); return; }

    const group = document.createElement('section');
    group.className = 'lds-hub-filtered__group lds-hub-filtered__group--grille';
    group.dataset.ldsGrilleModelResult = 'true';
    group.setAttribute('aria-labelledby', 'lds-grille-model-result-title');

    const heading = document.createElement('div');
    heading.className = 'lds-hub-filtered__heading';
    const title = document.createElement('h3');
    title.id = 'lds-grille-model-result-title';
    title.textContent = 'LED Grille Badges';
    heading.append(title);
    group.append(heading);

    const grid = document.createElement('div');
    grid.className = 'lds-hub-filtered__grid';
    cards.forEach(card => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('hidden');
      clone.removeAttribute('aria-hidden');
      clone.dataset.ldsGrilleModelCard = 'true';
      if (clone.hasAttribute('data-lds-grille-showcase')) {
        delete clone.dataset.grilleShowcaseReady;
        clone.querySelectorAll('[data-grille-showcase-designs]').forEach(designs => { designs.hidden = true; });
        clone.querySelectorAll('canvas').forEach(canvas => { canvas.hidden = true; });
        group.append(clone);
      } else grid.append(clone);
    });
    if (grid.children.length) group.append(grid);
    output.append(group);
    window.LDSGrilleHubShowcase?.mountAll();
    if (section) setHidden(section, true);
  };

  const processHub = hub => {
    if (!hub || hub.dataset.ldsGrilleModelFilterReady === 'true') return;
    hub.dataset.ldsGrilleModelFilterReady = 'true';
    filterKnownModelCards(hub);

    hub.addEventListener('click', event => {
      if (event.target.closest('[data-hub-reset]')) {
        window.setTimeout(() => removeBridge(hub), 0);
      }
    });
    hub.addEventListener('shopify:section:load', () => {
      window.setTimeout(() => renderBridge(hub), 0);
    });
    const label = hub.querySelector('[data-hub-selected-label]');
    const output = hub.querySelector('[data-hub-filtered-badges]');
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; renderBridge(hub); });
    };
    if (label) new MutationObserver(schedule).observe(label, {childList: true, characterData: true, subtree: true});
    if (output) new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes]
        .some(node => node.nodeType === 1 && !node.hasAttribute('data-lds-grille-model-result')))) schedule();
    }).observe(output, {childList: true});
    hub.addEventListener('change', event => {
      if (event.target.matches('[data-vehicle-model],[data-vehicle-generation]')) setTimeout(schedule, 200);
    });
    renderBridge(hub);
  };

  const boot = () => {
    document.querySelectorAll('[data-lds-accessories-hub]').forEach(processHub);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
